// Copyright Epic Games, Inc. All Rights Reserved.
import React from 'react';
import { MDXRemote } from 'next-mdx-remote';
import { AppLayout } from '@layout/index';
import { getBreadCrumbsArrayForPage } from '@utils/navigation/search';
import { withAdditionalComponentData, withComponentsToInject } from '@utils/mdx/components';
import { useTranslationResponse } from '@isomorphic/hooks/use-translation-response';
import { useSendAnalytics } from '@client/api/use-send-analytics';
import { EOSDocsHead } from '@layout/components/EOSDocsHead/EOSDocsHead';
import { TOCProvider } from '@contexts/ToC';
import { useProtectedContent } from '@contexts/ProtectedContent';
import { useUserGatingContext } from '@contexts/UserGating';
import { useNavigationBar } from '@client/api/use-navigation-bar';
import { useNotFound } from '@client/hooks/use-not-found';
import type { DocumentationPageProps } from '@pages/[...slug]';

export const DocumentPageView: React.FC<DocumentationPageProps> = ({
  tableOfContentsMap,
  pageSource,
  partialsSources,
  document,
  i18nContext,
  navTree,
  timeToRead,
  publicRuntimeSettings,
  seoConfig,
  activeDocMeta,
  isProtected,
  notFound,
}) => {
  useNotFound(notFound);
  useSendAnalytics();
  const { navTreeData: navTreeRevalidated } = useNavigationBar();
  const pageTitle = useTranslationResponse(activeDocMeta?.title || activeDocMeta?.navTitle, 'navigation');

  // For protected documents the static props ship an empty body. After the gating
  // wrapper has authenticated the user and fetched the real content from
  // /docs/api/protected-content, it populates ProtectedContentContext and we swap
  // in those values. Non-protected pages get null override and render from static props.
  const override = useProtectedContent();
  const { isLoading: gatingIsLoading } = useUserGatingContext();
  const effectivePageSource = override?.pageSource ?? pageSource;
  const effectivePartialsSources = override?.partialsSources ?? partialsSources;
  const effectiveTOC = override?.tableOfContentsMap ?? tableOfContentsMap;
  const effectiveTimeToRead = override?.timeToRead ?? timeToRead;

  const breadcrumbsRevalidated = navTreeRevalidated
    ? getBreadCrumbsArrayForPage(navTreeRevalidated, activeDocMeta?.slug)
    : getBreadCrumbsArrayForPage(navTree, activeDocMeta?.slug);

  if (notFound) {
    return null;
  }

  const componentsToInject = withComponentsToInject(document, effectivePartialsSources);
  const components = withAdditionalComponentData({ componentsToInject, document });
  const seoConfigWithTranslatedPageTitle = {
    ...seoConfig,
    title: seoConfig.title || pageTitle,
    openGraph: {
      ...seoConfig.openGraph,
      title: seoConfig.openGraph?.title || pageTitle,
    },
  };

  const isAwaitingProtectedContent = isProtected && override === null && gatingIsLoading;

  return (
    <>
      <EOSDocsHead seoConfig={seoConfigWithTranslatedPageTitle} />
      <TOCProvider tableOfContentsMap={effectiveTOC}>
        <AppLayout
          {...document}
          navTree={navTreeRevalidated || navTree}
          activeDocMeta={activeDocMeta}
          publicRuntimeSettings={publicRuntimeSettings}
          timeToRead={effectiveTimeToRead}
          i18nContext={i18nContext}
          breadcrumbs={breadcrumbsRevalidated}
          seoConfig={seoConfigWithTranslatedPageTitle}
        >
          {isAwaitingProtectedContent ? null : <MDXRemote {...effectivePageSource} components={components as any} />}
        </AppLayout>
      </TOCProvider>
    </>
  );
};
